import React from "react";
import { IS_DEV_UI_ENABLED } from "../utils/devUi";
import {
  handleChunkLoadErrorRecovery,
  isChunkLoadError,
} from "../utils/chunkLoadRecovery";

const createErrorId = () =>
  `FQ-${new Date().toISOString().replace(/[-:.TZ]/g, "").slice(0, 14)}`;

const getAppErrorRuntimeDetails = () => ({
  appVersion: process.env.REACT_APP_VERSION || "unknown",
  buildTimestamp: process.env.REACT_APP_BUILD_TIMESTAMP || "unknown",
  deployedCommit: process.env.REACT_APP_GIT_SHA || "unknown",
  route: `${window.location.pathname || "/"}${window.location.search || ""}${
    window.location.hash || ""
  }`,
  url: window.location.href,
  userAgent: navigator.userAgent || "",
  at: new Date().toISOString(),
});

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      errorInfo: null,
      errorId: "",
      copied: false,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      error,
      errorId: createErrorId(),
    };
  }

  componentDidCatch(error, errorInfo) {
    handleChunkLoadErrorRecovery(error, {
      source: "AppErrorBoundary",
      autoReload: true,
    });
    this.setState({ errorInfo });
    console.error("FABRIQUOTE_APP_RENDER_ERROR", {
      errorId: this.state.errorId,
      message: error?.message || "",
      stack: error?.stack || "",
      componentStack: errorInfo?.componentStack || "",
      ...getAppErrorRuntimeDetails(),
    });
  }

  copyErrorDetails = () => {
    const { error, errorInfo, errorId } = this.state;
    const runtimeDetails = getAppErrorRuntimeDetails();
    const supportSafeDetails = {
      errorId,
      message: error?.message || "Application render error",
      appVersion: runtimeDetails.appVersion,
      buildTimestamp: runtimeDetails.buildTimestamp,
      deployedCommit: runtimeDetails.deployedCommit,
      route: runtimeDetails.route,
      url: runtimeDetails.url,
      userAgent: runtimeDetails.userAgent,
      at: runtimeDetails.at,
    };
    const developerDetails = {
      ...supportSafeDetails,
      stack: error?.stack || "",
      componentStack: errorInfo?.componentStack || "",
    };
    const details = JSON.stringify(
      IS_DEV_UI_ENABLED ? developerDetails : supportSafeDetails,
      null,
      2
    );

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(details);
      this.setState({ copied: true });
    }
  };

  render() {
    const { error, errorInfo, errorId, copied } = this.state;
    const isDev = IS_DEV_UI_ENABLED;
    const chunkLoadFailed = isChunkLoadError(error);

    if (!error) {
      return this.props.children;
    }

    return (
      <main
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: 24,
          background: "#020617",
          color: "#e2e8f0",
          fontFamily:
            "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif",
        }}
      >
        <section
          style={{
            width: "min(680px, 100%)",
            display: "grid",
            gap: 16,
            padding: 24,
            borderRadius: 22,
            background: "rgba(15,23,42,0.92)",
            border: "1px solid rgba(148,163,184,0.28)",
            boxShadow: "0 24px 80px rgba(0,0,0,0.42)",
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#93c5fd",
            }}
          >
            Application Error
          </div>
          <h1 style={{ margin: 0, fontSize: 28, lineHeight: 1.1 }}>
            {chunkLoadFailed
              ? "FabriQuote was updated. Please reload once to continue."
              : "FabriQuote could not load because of an application error."}
          </h1>
          <p style={{ margin: 0, color: "#cbd5e1", lineHeight: 1.6 }}>
            {chunkLoadFailed
              ? "Your browser has an older app file cached, or the network could not load a required app chunk."
              : "Please reload the page. If it keeps happening, share this error id with support:"}{" "}
            <strong>{errorId}</strong>
          </p>
          {isDev ? (
            <details
              open
              style={{
                padding: 14,
                borderRadius: 14,
                background: "rgba(2,6,23,0.72)",
                border: "1px solid rgba(148,163,184,0.2)",
              }}
            >
              <summary style={{ cursor: "pointer", fontWeight: 800 }}>
                Developer details
              </summary>
              <pre style={{ whiteSpace: "pre-wrap", overflow: "auto", fontSize: 12 }}>
                {`${error?.message || ""}\n\n${error?.stack || ""}\n\n${
                  errorInfo?.componentStack || ""
                }`}
              </pre>
            </details>
          ) : null}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                border: 0,
                borderRadius: 12,
                padding: "12px 16px",
                background: "#2563eb",
                color: "#fff",
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.copyErrorDetails}
              style={{
                border: "1px solid rgba(148,163,184,0.36)",
                borderRadius: 12,
                padding: "12px 16px",
                background: "rgba(15,23,42,0.72)",
                color: "#e2e8f0",
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              {copied ? "Copied" : "Copy Support Details"}
            </button>
          </div>
        </section>
      </main>
    );
  }
}

export default AppErrorBoundary;
